/**
 * `OpBlockOverlay` — the IDE-wide modal block shown while a resource copy/move
 * is in flight (operator brief 2026-07-03). Mounted once near the app shell
 * (next to `ToastOverlay` / `CopyMoveDropOverlay`); invisible until
 * `useOpBlockStore.pending > 0`.
 *
 * These ops are infrequent but not instant, and the fast paths (send-to
 * popover, drag-drop) have no in-flight surface of their own — so we block the
 * whole UI with a centered spinner + an op-specific label until the op AND its
 * destination/source tree refetch have settled. Success/failure is then
 * reported by the toast the op drops. `z-[100]` sits above every dialog
 * (dialogs use z-50).
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useOpBlockStore } from "@/stores/useOpBlockStore";
import { useToastStore } from "@/stores/useToastStore";

/** Absolute upper bound (ms) on the IDE-blocking overlay — a watchdog backstop
 *  so no lifecycle gap can wedge the UI. Comfortably above any legit op yet a
 *  hard ceiling; the normal path clears far sooner via the mutation lifecycle. */
const OP_BLOCK_WATCHDOG_MS = 30_000;

export function OpBlockOverlay() {
  const { t } = useTranslation();
  const pending = useOpBlockStore((s) => s.pending);
  const kind = useOpBlockStore((s) => s.kind);
  const reset = useOpBlockStore((s) => s.reset);
  const addToast = useToastStore((s) => s.addToast);

  // Watchdog — if a block is up longer than the ceiling (a hung socket, a
  // lifecycle gap), force it down + inform the user rather than lock the IDE.
  useEffect(() => {
    if (pending <= 0) return;
    const id = window.setTimeout(() => {
      reset();
      addToast({
        text: t("resourceCopyMove.opTimeout"),
        kind: "error",
        autoDismissMs: 7000,
      });
    }, OP_BLOCK_WATCHDOG_MS);
    return () => window.clearTimeout(id);
  }, [pending, reset, addToast, t]);

  if (pending <= 0) return null;

  const label =
    kind === "move"
      ? t("resourceCopyMove.moving")
      : t("resourceCopyMove.copying");

  return (
    <div
      data-testid="op-block-overlay"
      role="alertdialog"
      aria-busy="true"
      aria-live="assertive"
      aria-label={label}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-coffee-900/40 backdrop-blur-[1px] dark:bg-coffee-950/60"
    >
      <div className="flex items-center gap-3 rounded-2xl border border-cream-200/70 bg-white px-5 py-4 shadow-[0_8px_40px_rgba(28,24,20,0.18)] dark:border-coffee-700/60 dark:bg-coffee-900">
        <Loader2 size={18} className="animate-spin text-gold-600 dark:text-gold-400" />
        <span className="text-body-sm font-medium text-coffee-900 dark:text-cream-100">
          {label}
        </span>
      </div>
    </div>
  );
}
